import { sanitizeReturnTo } from "@repo/shared";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { authClient } from "@/lib/auth-client";
import { useT } from "@/lib/i18n/use-t";
import { getRememberedOrgSlug, rememberOrgSlug } from "@/lib/remembered-org";
import { T } from "./page.i18n";

export default function LogoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useT(T);
  const [status, setStatus] = useState(t.loggingOut);
  // QL-261 — AppShell sends stale-cookie visitors here with the URL they were
  // opening; forward it to the login page so it survives the round trip.
  // Origin-locked: a forged value can never send the user off-site.
  const redirect = sanitizeReturnTo(searchParams.get("redirect"), window.location.origin);

  useEffect(() => {
    async function performLogout() {
      try {
        // Try to sign out via Better Auth
        await authClient.signOut();
        setStatus(t.signedOut);
      } catch (_e) {
        // Even if signOut fails, continue with cleanup
      }

      // Clear all cookies manually as backup
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const name = cookie.split("=")[0].trim();
        // Clear cookie for all possible paths
        // biome-ignore lint/suspicious/noDocumentCookie: Need direct cookie manipulation for logout cleanup
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        // biome-ignore lint/suspicious/noDocumentCookie: Need direct cookie manipulation for logout cleanup
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
      }

      // Clear localStorage and sessionStorage, but keep the remembered
      // organization so the user skips the tenant step on next login
      try {
        const rememberedOrg = getRememberedOrgSlug();
        localStorage.clear();
        sessionStorage.clear();
        if (rememberedOrg) {
          rememberOrgSlug(rememberedOrg);
        }
      } catch (_e) {
        // Ignore storage errors
      }

      setStatus(t.redirecting);

      // Small delay to show the message, then redirect
      setTimeout(() => {
        // A redirect that is itself the login page (an OAuth sign-in carried through the
        // wrong-organization overlay) is opened as is: wrapped in ?redirect= it would
        // land on that login page again after signing in, instead of finishing it.
        const isLoginPage = redirect?.startsWith("/login?") ?? false;
        navigate(
          isLoginPage && redirect
            ? redirect
            : redirect && redirect !== "/"
              ? `/login?redirect=${encodeURIComponent(redirect)}`
              : "/login",
        );
      }, 500);
    }

    performLogout();
  }, [navigate, t, redirect]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  );
}
